"use client";

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { FadeIn } from "@/components/ui/motion";
import { useLang, useLangPrefix } from "@/contexts/lang";
import type { SectionIds } from "@/data/home";

type AboutSectionProps = {
	sectionIds: SectionIds;
};

export function AboutSection({ sectionIds }: AboutSectionProps) {
	const t = useLang();
	const prefix = useLangPrefix();

	return (
		<section
			id={sectionIds.about}
			className="scroll-mt-32 bg-cream relative py-20 sm:py-24 md:py-32 lg:py-40 overflow-hidden mt-paper"
		>
			{/* Subtle corner accent */}
			<div className="absolute bottom-0 left-0 w-[300px] h-[300px] hidden lg:block pointer-events-none opacity-40">
				<svg
					viewBox="0 0 300 300"
					fill="none"
					className="w-full h-full"
					aria-hidden="true"
				>
					<path
						d="M0 300 L0 0 L300 0"
						stroke="rgba(201, 161, 90, 0.15)"
						strokeWidth="1"
						fill="none"
					/>
				</svg>
			</div>

			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
				<div className="grid gap-12 md:gap-16 lg:gap-24 lg:grid-cols-12 lg:items-start">
					{/* Left column */}
					<div className="lg:col-span-5">
						<FadeIn direction="right">
							<div className="lg:sticky lg:top-36">
								<div className="flex items-center gap-4 mb-6 md:mb-8">
									<span className="h-px w-10 sm:w-16 bg-accent" />
									<span className="text-label text-accent">
										{t.aboutSection.label}
									</span>
								</div>

								<h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-primary leading-[1.1] tracking-tight">
									{t.aboutSection.heading1}
									<span className="block mt-1 font-semibold text-primary/60">
										{t.aboutSection.heading2}
									</span>
								</h2>
							</div>
						</FadeIn>
					</div>

					{/* Right column */}
					<div className="lg:col-span-7">
						<FadeIn delay={0.2}>
							<div className="space-y-6 mb-10 md:mb-14">
								<p className="text-lg sm:text-xl md:text-2xl text-primary/80 leading-relaxed">
									{t.aboutSection.p1}
								</p>
								<p className="text-base sm:text-lg text-primary/60 leading-relaxed">
									{t.aboutSection.p2}
								</p>
							</div>
						</FadeIn>

						<FadeIn delay={0.35}>
							<div className="pt-6 md:pt-8 border-t border-primary/5">
								<Link
									to={prefix === "" ? "/about" : "/en/about"}
									className="group inline-flex items-center gap-4 text-sm font-medium tracking-wide text-primary transition-colors hover:text-accent"
								>
									<span>{t.aboutSection.cta}</span>
									<span className="flex items-center gap-2">
										<span className="h-px w-8 bg-primary/20 transition-all duration-500 group-hover:w-12 group-hover:bg-accent" />
										<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
									</span>
								</Link>
							</div>
						</FadeIn>
					</div>
				</div>
			</div>
		</section>
	);
}
